import {
  FaDatabase,
  FaHandshake,
  FaUserEdit,
  FaPlusCircle,
} from "react-icons/fa";
import OrdersTable from "../orders/components/orders-table";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { Role } from "@/core/auth/models/role.model";
import withAuthorization from "@/core/auth/components/withAuthorization";
import { Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDashboardMetrics } from "./services/dashboard.service";
import { fetchTransactionsStats } from "@/features/transactions/services/transactions.service";
import { TransactionStatsCard } from "../transactions/components/transaction-stats-card";

function Dashboard() {
  const auth = useAuth();

  const endDateObj = useMemo(() => new Date(), []);
  const startDateObj = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);
  const startDate = startDateObj.toISOString().split("T")[0];
  const endDate = endDateObj.toISOString().split("T")[0];
  const {
    data: dashboardMetrics = {
      totalSales: 0,
      totalOrders: 0,
      totalStaff: 0,
      totalServices: 0,
    },
    isError: isMetricsError,
    error: metricsError,
  } = useQuery({
    queryKey: ["dashboard-metrics", startDate, endDate],
    queryFn: () => fetchDashboardMetrics(startDate, endDate),
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: transactionStats,
    isError: isStatsError,
    error: statsError,
  } = useQuery({
    queryKey: ["transaction-stats", startDate, endDate],
    queryFn: () => fetchTransactionsStats(),
    staleTime: 1000 * 60 * 5,
  });

  const data = [
    {
      id: 1,
      title: "Last 30 Days Turnover",
      content: "Revenue",
      value: `KES ${dashboardMetrics.totalSales.toLocaleString()}`,
      icon: <FaDatabase className="text-2xl text-secondary" />,
      bg: "bg-gradient-to-r from-accent to-accent",
      textColor: "text-blue-600",
    },
    {
      id: 3,
      title: "Total Orders",
      content: "Orders",
      value: dashboardMetrics.totalOrders,
      icon: <Wrench className="text-2xl text-secondary" />,
      bg: "bg-gradient-to-r from-accent to-accent",
      textColor: "text-indigo-600",
    },
    {
      id: 4,
      title: "Active Employees",
      content: "Current staff",
      value: dashboardMetrics.totalStaff,
      icon: <FaUserEdit className="text-2xl text-secondary" />,
      bg: "bg-gradient-to-r from-accent to-accent",
      textColor: "text-red-600",
    },
    {
      id: 5,
      title: "Total Services",
      content: "Services",
      value: dashboardMetrics.totalServices,
      icon: <FaPlusCircle className="text-2xl text-secondary" />,
      bg: "bg-gradient-to-r from-accent to-accent",
      textColor: "text-green-600",
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold ">
            Welcome back,{" "}
            <span className="text-secondary">{auth.user?.email}</span>
          </h1>
        </div>

        {(isMetricsError || isStatsError) && (
          <div className="mb-6 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {metricsError?.message ||
              statsError?.message ||
              "Failed to load dashboard data"}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.map((item, index) => (
            <Card
              key={item.id + index}
              className="text-secondary dark:text-secondary rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border"
            >
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`${item.bg} p-3 rounded-lg shadow-lg`}>
                    <div className="">{item.icon}</div>
                  </div>
                  <span className={`text-sm font-semibold`}>
                    {item.content}
                  </span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-secondary">
                    {item.value}
                  </h3>
                  <p className="text-sm text-primary">{item.title}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {transactionStats && (
          <>
            <h2 className="text-xl font-semibold mt-10 mb-4">
              Transaction Stats
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <TransactionStatsCard
                title={"Today"}
                stats={{
                  cash: transactionStats.today.cash,
                  pdq: transactionStats.today.pdq,
                }}
              />
              <TransactionStatsCard
                title={"Last 7 days"}
                stats={{
                  cash: transactionStats.last7days.cash,
                  pdq: transactionStats.last7days.pdq,
                }}
              />
              <TransactionStatsCard
                title={"Last 30 days"}
                stats={{
                  cash: transactionStats.last30days.cash,
                  pdq: transactionStats.last30days.pdq,
                }}
              />
            </div>
          </>
        )}

        <div className="mt-10  rounded-xl shadow-sm">
          <h2 className="text-xl font-semibold mb-4 ">Current Orders</h2>
          <OrdersTable />
        </div>
      </div>
    </div>
  );
}

const DashboardPage = withAuthorization(Dashboard, [
  Role.CompanyAdmin,
  Role.SuperAdmin,
  Role.Employee,
]);

export default DashboardPage;
